/**
 * /knowledge/folder/$folderId — artigos de uma pasta: breadcrumb, header com
 * emoji/nome/descrição, busca central (filtro client-side), CTA e ordenação.
 */
import { useMemo, useState } from 'react';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import {
  errorMessage,
  knowledgeFoldersOptions,
  knowledgeListOptions,
} from '@/features/knowledge/knowledge-queries';
import { KnowledgeArticleList } from '@/features/knowledge/knowledge-article-list';

const routeApi = getRouteApi('/_authenticated/knowledge/folder/$folderId');

type Sort = 'az' | 'recent';

export function KnowledgeFolderPage() {
  const { folderId: folderIdParam } = routeApi.useParams();
  const folderId = Number(folderIdParam);
  const navigate = useNavigate();

  const [term, setTerm] = useState('');
  const [sort, setSort] = useState<Sort>('recent');

  const folders = useQuery(knowledgeFoldersOptions());
  const list = useQuery(knowledgeListOptions({ folder_id: folderId, sort }));
  const folder = folders.data?.find((f) => f.id === folderId);

  // Busca dentro da pasta: filtro client-side sobre a lista carregada.
  const filtered = useMemo(() => {
    const items = list.data ?? [];
    const q = term.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.excerpt.toLowerCase().includes(q) ||
        item.tags.some((tag) => tag.toLowerCase().includes(q))
    );
  }, [list.data, term]);

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-[13px] text-muted-foreground" aria-label="Breadcrumb">
        <Link to="/knowledge" className="hover:text-foreground">
          Conhecimentos
        </Link>
        <span className="mx-1.5">/</span>
        <span className="text-foreground">{folder?.name ?? '…'}</span>
      </nav>

      {/* Header da pasta */}
      <div className="flex flex-wrap items-start gap-3">
        <span className="text-[28px] leading-none" aria-hidden>
          {folder?.icon || '📁'}
        </span>
        <div className="min-w-0 flex-1">
          {folder ? (
            <>
              <h1 className="text-xl leading-7 font-semibold">{folder.name}</h1>
              {folder.description ? (
                <p className="mt-0.5 text-sm text-muted-foreground">{folder.description}</p>
              ) : null}
            </>
          ) : (
            <Skeleton className="h-7 w-56" />
          )}
        </div>
        <Button
          size="sm"
          onClick={() => void navigate({ to: '/knowledge/new', search: { folder: folderId } })}
        >
          <Plus className="size-4" strokeWidth={2.5} />
          Conhecimento
        </Button>
      </div>

      {/* Busca central */}
      <div className="relative mx-auto w-full max-w-2xl">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Busque por um conhecimento nesta pasta"
          aria-label="Buscar conhecimento na pasta"
          className="bg-card pl-9"
        />
      </div>

      {/* Ordenação + lista */}
      <div className="mx-auto w-full max-w-2xl space-y-3">
        <div className="flex items-center justify-end gap-2">
          <label htmlFor="kb-sort" className="text-[13px] text-muted-foreground">
            Ordenar por:
          </label>
          <Select
            id="kb-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value as Sort)}
            className="h-8 w-32"
          >
            <option value="az">A-Z</option>
            <option value="recent">Recentes</option>
          </Select>
        </div>

        {list.isPending ? (
          <div aria-busy className="flex flex-col gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-start gap-3 rounded-lg border bg-card p-4 shadow-xs">
                <Skeleton className="size-9 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-64" />
                  <Skeleton className="h-3.5 w-32" />
                  <Skeleton className="h-3.5 w-3/4" />
                </div>
              </div>
            ))}
          </div>
        ) : list.isError ? (
          <EmptyState message={`Erro ao carregar conhecimentos: ${errorMessage(list.error)}`} />
        ) : filtered.length === 0 ? (
          <EmptyState
            message={
              term.trim()
                ? 'Nenhum conhecimento encontrado para a busca'
                : 'Nenhum conhecimento nesta pasta'
            }
          />
        ) : (
          <KnowledgeArticleList items={filtered} />
        )}
      </div>
    </div>
  );
}
